// Multi-language client code generators.
//
// Each generator turns a normalised request spec into a copy-pasteable
// snippet for its target library. Generators only ever do string
// composition — they never reach over to the network. The same RequestSpec
// shape is shared with @rest/curl so the SPA can flip between curl and any
// other generator without re-massaging data.

import { emit as emitCurl, type RequestSpec as CurlSpec } from "@rest/curl";

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "HEAD" | "OPTIONS";
export type BodyType = "none" | "json" | "text" | "form" | "multipart";

export interface KV { key: string; value: string; enabled?: boolean }

export interface RequestSpec {
  name: string;
  method: Method;
  url: string;
  headers: KV[];
  query: KV[];
  bodyType: BodyType;
  body: string | null;
  auth?: { kind: "none" | "basic" | "bearer"; fields: Record<string, string> };
}

export interface Generator {
  id: string;        // "javascript-fetch"
  label: string;     // "JavaScript — fetch"
  lang: string;      // "javascript"
  hint?: string;     // installation note shown above the code
  ext: string;       // "js", "ts", "py", "go", "rb", "cs", "java", "rs", "swift", "php", "ps1"
  generate: (spec: RequestSpec) => string;
}

// ── helpers ───────────────────────────────────────────────────────────

function enabledHeaders(spec: RequestSpec): KV[] {
  return spec.headers.filter((h) => h.enabled !== false && h.key);
}
function enabledQuery(spec: RequestSpec): KV[] {
  return spec.query.filter((q) => q.enabled !== false && q.key);
}
function fullUrl(spec: RequestSpec): string {
  const qs = enabledQuery(spec);
  if (!qs.length) return spec.url;
  try {
    const u = new URL(spec.url);
    for (const kv of qs) u.searchParams.append(kv.key, kv.value);
    return u.toString();
  } catch {
    const tail = qs.map((kv) => `${encodeURIComponent(kv.key)}=${encodeURIComponent(kv.value)}`).join("&");
    return spec.url + (spec.url.includes("?") ? "&" : "?") + tail;
  }
}
function authHeader(spec: RequestSpec): KV | null {
  const a = spec.auth;
  if (!a || a.kind === "none") return null;
  if (a.kind === "basic") {
    const user = a.fields.username ?? "";
    const pass = a.fields.password ?? "";
    return { key: "Authorization", value: `Basic ${b64(`${user}:${pass}`)}` };
  }
  if (a.kind === "bearer") {
    const t = a.fields.token ?? "";
    return { key: "Authorization", value: `Bearer ${t}` };
  }
  return null;
}
function combinedHeaders(spec: RequestSpec): KV[] {
  const seen = new Set(spec.headers.map((h) => h.key.toLowerCase()));
  const ah = authHeader(spec);
  const headers = enabledHeaders(spec).slice();
  if (ah && !seen.has(ah.key.toLowerCase())) headers.push(ah);
  return headers;
}
function jsStr(s: string): string {
  return JSON.stringify(s);
}
function bodyHasContent(spec: RequestSpec): boolean {
  return spec.bodyType !== "none" && spec.body != null && spec.body !== "";
}
function tryJson(s: string): unknown | null {
  try { return JSON.parse(s); } catch { return null; }
}
function b64(s: string): string {
  if (typeof btoa === "function") return btoa(unescape(encodeURIComponent(s)));
  return Buffer.from(s, "utf8").toString("base64");
}
function multipartPairs(spec: RequestSpec): Array<{ key: string; value: string }> {
  // Multipart bodies are stored as "k=v\nk=v" lines.
  return (spec.body ?? "").split("\n").map((line) => {
    const i = line.indexOf("=");
    if (i < 0) return null;
    return { key: line.slice(0, i), value: line.slice(i + 1) };
  }).filter((x): x is { key: string; value: string } => !!x && !!x.key);
}

// ── JavaScript / TypeScript ───────────────────────────────────────────

function jsFetch(spec: RequestSpec, opts: { typed?: boolean } = {}): string {
  const lines: string[] = [];
  const url = fullUrl(spec);
  const headers = combinedHeaders(spec);

  const headerLines = headers.length
    ? "  headers: {\n" + headers.map((h) => `    ${jsStr(h.key)}: ${jsStr(h.value)},`).join("\n") + "\n  },"
    : "";

  let bodyLine = "";
  if (bodyHasContent(spec)) {
    if (spec.bodyType === "json") {
      const v = tryJson(spec.body!);
      bodyLine = v == null
        ? `  body: ${jsStr(spec.body!)},`
        : `  body: JSON.stringify(${JSON.stringify(v, null, 2).replace(/\n/g, "\n  ")}),`;
    } else if (spec.bodyType === "form") {
      bodyLine = `  body: new URLSearchParams(${jsStr(spec.body!)}),`;
    } else if (spec.bodyType === "multipart") {
      lines.push("const form = new FormData();");
      for (const p of multipartPairs(spec)) {
        lines.push(`form.append(${jsStr(p.key)}, ${jsStr(p.value)});`);
      }
      lines.push("");
      bodyLine = "  body: form,";
    } else {
      bodyLine = `  body: ${jsStr(spec.body!)},`;
    }
  }

  const cast = opts.typed ? ": Response" : "";
  lines.push(`const res${cast} = await fetch(${jsStr(url)}, {`);
  lines.push(`  method: ${jsStr(spec.method)},`);
  if (headerLines) lines.push(headerLines);
  if (bodyLine) lines.push(bodyLine);
  lines.push("});");
  lines.push("");
  lines.push(opts.typed
    ? `const data: unknown = await res.json();`
    : "const data = await res.json();");
  lines.push("console.log(res.status, data);");
  return lines.join("\n");
}

function axiosGen(spec: RequestSpec): string {
  const lines: string[] = [`import axios from "axios";`, ""];
  const headers = combinedHeaders(spec);
  lines.push("const res = await axios({");
  lines.push(`  method: ${jsStr(spec.method.toLowerCase())},`);
  lines.push(`  url: ${jsStr(spec.url)},`);
  const qs = enabledQuery(spec);
  if (qs.length) {
    lines.push("  params: {");
    for (const q of qs) lines.push(`    ${jsStr(q.key)}: ${jsStr(q.value)},`);
    lines.push("  },");
  }
  if (headers.length) {
    lines.push("  headers: {");
    for (const h of headers) lines.push(`    ${jsStr(h.key)}: ${jsStr(h.value)},`);
    lines.push("  },");
  }
  if (bodyHasContent(spec)) {
    if (spec.bodyType === "json") {
      const v = tryJson(spec.body!);
      lines.push(`  data: ${v == null ? jsStr(spec.body!) : JSON.stringify(v, null, 2).replace(/\n/g, "\n  ")},`);
    } else if (spec.bodyType === "form") {
      lines.push(`  data: new URLSearchParams(${jsStr(spec.body!)}),`);
    } else if (spec.bodyType === "multipart") {
      lines[0] = `import axios from "axios";\nimport FormData from "form-data";`;
      lines.push("  data: (() => {");
      lines.push("    const form = new FormData();");
      for (const p of multipartPairs(spec)) {
        lines.push(`    form.append(${jsStr(p.key)}, ${jsStr(p.value)});`);
      }
      lines.push("    return form;");
      lines.push("  })(),");
    } else {
      lines.push(`  data: ${jsStr(spec.body!)},`);
    }
  }
  lines.push("});");
  lines.push("");
  lines.push("console.log(res.status, res.data);");
  return lines.join("\n");
}

// ── Python ───────────────────────────────────────────────────────────

function pyRequests(spec: RequestSpec): string {
  const lines: string[] = [`import requests`, ""];
  const headers = combinedHeaders(spec);
  const params = enabledQuery(spec);
  const args: string[] = [`${pyStr(spec.url)}`];
  if (params.length) {
    args.push(`params=${pyDict(params)}`);
  }
  if (headers.length) {
    args.push(`headers=${pyDict(headers)}`);
  }
  if (bodyHasContent(spec)) {
    if (spec.bodyType === "json") {
      const v = tryJson(spec.body!);
      args.push(`json=${v == null ? pyStr(spec.body!) : pyValue(v)}`);
    } else if (spec.bodyType === "form") {
      args.push(`data=${pyStr(spec.body!)}`);
    } else if (spec.bodyType === "multipart") {
      const pairs = multipartPairs(spec).map((p) => `${pyStr(p.key)}: (None, ${pyStr(p.value)})`).join(", ");
      args.push(`files={${pairs}}`);
    } else {
      args.push(`data=${pyStr(spec.body!)}`);
    }
  }
  lines.push(`res = requests.${spec.method.toLowerCase()}(`);
  for (const a of args) lines.push(`    ${a},`);
  lines.push(`)`);
  lines.push(``);
  lines.push(`print(res.status_code, res.json())`);
  return lines.join("\n");
}

function pyHttpx(spec: RequestSpec): string {
  const lines: string[] = [`import httpx`, "", "async def main():"];
  const headers = combinedHeaders(spec);
  const params = enabledQuery(spec);
  lines.push(`    async with httpx.AsyncClient() as client:`);
  const args: string[] = [`${pyStr(spec.url)}`];
  if (params.length) args.push(`params=${pyDict(params)}`);
  if (headers.length) args.push(`headers=${pyDict(headers)}`);
  if (bodyHasContent(spec)) {
    if (spec.bodyType === "json") {
      const v = tryJson(spec.body!);
      args.push(`json=${v == null ? pyStr(spec.body!) : pyValue(v)}`);
    } else if (spec.bodyType === "form") {
      args.push(`data=${pyStr(spec.body!)}`);
    } else if (spec.bodyType === "multipart") {
      const pairs = multipartPairs(spec).map((p) => `${pyStr(p.key)}: (None, ${pyStr(p.value)})`).join(", ");
      args.push(`files={${pairs}}`);
    } else {
      args.push(`content=${pyStr(spec.body!)}`);
    }
  }
  lines.push(`        res = await client.${spec.method.toLowerCase()}(`);
  for (const a of args) lines.push(`            ${a},`);
  lines.push(`        )`);
  lines.push(`        print(res.status_code, res.json())`);
  lines.push(``);
  lines.push(`# import asyncio; asyncio.run(main())`);
  return lines.join("\n");
}

function pyStr(s: string): string {
  // Use repr-ish: single-quote and escape carefully.
  return JSON.stringify(s);
}
function pyDict(kvs: KV[]): string {
  return "{\n" + kvs.map((kv) => `        ${pyStr(kv.key)}: ${pyStr(kv.value)},`).join("\n") + "\n    }";
}
function pyValue(v: unknown, indent = "    "): string {
  if (v === null) return "None";
  if (typeof v === "boolean") return v ? "True" : "False";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") return JSON.stringify(v);
  if (Array.isArray(v)) {
    const inner = v.map((x) => pyValue(x, indent + "    ")).join(", ");
    return `[${inner}]`;
  }
  if (typeof v === "object") {
    const entries = Object.entries(v as Record<string, unknown>);
    if (!entries.length) return "{}";
    return "{\n" + entries.map(([k, val]) => `${indent}    ${JSON.stringify(k)}: ${pyValue(val, indent + "    ")},`).join("\n") + `\n${indent}}`;
  }
  return JSON.stringify(v);
}

// ── Go ───────────────────────────────────────────────────────────────

function goNetHttp(spec: RequestSpec): string {
  const url = fullUrl(spec);
  const lines: string[] = [];
  lines.push(`package main`, ``, `import (`);
  lines.push(`    "fmt"`);
  if (bodyHasContent(spec)) lines.push(`    "strings"`);
  lines.push(`    "io"`);
  lines.push(`    "net/http"`);
  lines.push(`)`, ``);
  lines.push(`func main() {`);
  if (bodyHasContent(spec)) {
    lines.push(`    body := strings.NewReader(${goStr(spec.body!)})`);
    lines.push(`    req, _ := http.NewRequest(${goStr(spec.method)}, ${goStr(url)}, body)`);
  } else {
    lines.push(`    req, _ := http.NewRequest(${goStr(spec.method)}, ${goStr(url)}, nil)`);
  }
  for (const h of combinedHeaders(spec)) {
    lines.push(`    req.Header.Set(${goStr(h.key)}, ${goStr(h.value)})`);
  }
  lines.push(`    res, err := http.DefaultClient.Do(req)`);
  lines.push(`    if err != nil { panic(err) }`);
  lines.push(`    defer res.Body.Close()`);
  lines.push(`    out, _ := io.ReadAll(res.Body)`);
  lines.push(`    fmt.Println(res.StatusCode, string(out))`);
  lines.push(`}`);
  return lines.join("\n");
}
function goStr(s: string): string { return JSON.stringify(s); }

// ── Ruby ─────────────────────────────────────────────────────────────

function rubyNetHttp(spec: RequestSpec): string {
  const lines: string[] = [];
  lines.push(`require 'net/http'`, `require 'uri'`, `require 'json'`, ``);
  lines.push(`uri = URI(${rbStr(fullUrl(spec))})`);
  const klass: Record<Method, string> = {
    GET: "Net::HTTP::Get", POST: "Net::HTTP::Post", PUT: "Net::HTTP::Put",
    PATCH: "Net::HTTP::Patch", DELETE: "Net::HTTP::Delete",
    HEAD: "Net::HTTP::Head", OPTIONS: "Net::HTTP::Options",
  };
  lines.push(`req = ${klass[spec.method]}.new(uri)`);
  for (const h of combinedHeaders(spec)) {
    lines.push(`req[${rbStr(h.key)}] = ${rbStr(h.value)}`);
  }
  if (bodyHasContent(spec)) {
    lines.push(`req.body = ${rbStr(spec.body!)}`);
  }
  lines.push(`res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: uri.scheme == 'https') do |http|`);
  lines.push(`  http.request(req)`);
  lines.push(`end`);
  lines.push(`puts res.code, res.body`);
  return lines.join("\n");
}
function rbStr(s: string): string { return JSON.stringify(s); }

// ── PHP — Guzzle ─────────────────────────────────────────────────────

function phpGuzzle(spec: RequestSpec): string {
  const lines: string[] = [`<?php`, ``, `require __DIR__ . '/vendor/autoload.php';`, ``, `$client = new GuzzleHttp\\Client();`, ``];
  const opts: string[] = [];
  const headers = combinedHeaders(spec);
  if (headers.length) {
    opts.push(`    'headers' => [`);
    for (const h of headers) opts.push(`        ${phpStr(h.key)} => ${phpStr(h.value)},`);
    opts.push(`    ],`);
  }
  const q = enabledQuery(spec);
  if (q.length) {
    opts.push(`    'query' => [`);
    for (const kv of q) opts.push(`        ${phpStr(kv.key)} => ${phpStr(kv.value)},`);
    opts.push(`    ],`);
  }
  if (bodyHasContent(spec)) {
    if (spec.bodyType === "json") {
      const v = tryJson(spec.body!);
      opts.push(`    'json' => ${v == null ? phpStr(spec.body!) : phpValue(v)},`);
    } else if (spec.bodyType === "form") {
      opts.push(`    'body' => ${phpStr(spec.body!)},`);
    } else if (spec.bodyType === "multipart") {
      opts.push(`    'multipart' => [`);
      for (const p of multipartPairs(spec)) {
        opts.push(`        ['name' => ${phpStr(p.key)}, 'contents' => ${phpStr(p.value)}],`);
      }
      opts.push(`    ],`);
    } else {
      opts.push(`    'body' => ${phpStr(spec.body!)},`);
    }
  }
  lines.push(`$res = $client->request(${phpStr(spec.method)}, ${phpStr(spec.url)}, [`);
  for (const o of opts) lines.push(o);
  lines.push(`]);`, ``, `echo $res->getStatusCode() . "\\n";`, `echo $res->getBody();`);
  return lines.join("\n");
}
function phpStr(s: string): string {
  return "'" + s.replace(/\\/g, "\\\\").replace(/'/g, "\\'") + "'";
}
function phpValue(v: unknown, indent = "    "): string {
  if (v === null) return "null";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") return phpStr(v);
  if (Array.isArray(v)) return `[${v.map((x) => phpValue(x, indent + "    ")).join(", ")}]`;
  if (typeof v === "object") {
    return "[\n" + Object.entries(v as Record<string, unknown>).map(([k, val]) => `${indent}    ${phpStr(k)} => ${phpValue(val, indent + "    ")},`).join("\n") + `\n${indent}]`;
  }
  return phpStr(String(v));
}

// ── Java 11 HttpClient ───────────────────────────────────────────────

function javaHttp(spec: RequestSpec): string {
  const lines: string[] = [];
  lines.push(`import java.net.URI;`);
  lines.push(`import java.net.http.HttpClient;`);
  lines.push(`import java.net.http.HttpRequest;`);
  lines.push(`import java.net.http.HttpResponse;`);
  lines.push(``);
  lines.push(`HttpClient client = HttpClient.newHttpClient();`);
  lines.push(``);
  lines.push(`HttpRequest req = HttpRequest.newBuilder()`);
  lines.push(`    .uri(URI.create(${javaStr(fullUrl(spec))}))`);
  const m = spec.method;
  if (bodyHasContent(spec) && m !== "GET" && m !== "DELETE" && m !== "HEAD") {
    lines.push(`    .method(${javaStr(m)}, HttpRequest.BodyPublishers.ofString(${javaStr(spec.body!)}))`);
  } else if (m === "GET") {
    lines.push(`    .GET()`);
  } else if (m === "DELETE") {
    lines.push(`    .DELETE()`);
  } else {
    lines.push(`    .method(${javaStr(m)}, HttpRequest.BodyPublishers.noBody())`);
  }
  for (const h of combinedHeaders(spec)) {
    lines.push(`    .header(${javaStr(h.key)}, ${javaStr(h.value)})`);
  }
  lines.push(`    .build();`);
  lines.push(``);
  lines.push(`HttpResponse<String> res = client.send(req, HttpResponse.BodyHandlers.ofString());`);
  lines.push(`System.out.println(res.statusCode());`);
  lines.push(`System.out.println(res.body());`);
  return lines.join("\n");
}
function javaStr(s: string): string { return JSON.stringify(s); }

// ── C# HttpClient ────────────────────────────────────────────────────

function csharpHttp(spec: RequestSpec): string {
  const lines: string[] = [];
  lines.push(`using System;`);
  lines.push(`using System.Net.Http;`);
  lines.push(`using System.Text;`);
  lines.push(`using System.Threading.Tasks;`);
  lines.push(``);
  lines.push(`var client = new HttpClient();`);
  lines.push(`var request = new HttpRequestMessage(new HttpMethod(${csStr(spec.method)}), ${csStr(fullUrl(spec))});`);
  for (const h of combinedHeaders(spec)) {
    // Most headers go on the request, but content-type must go on the content.
    if (h.key.toLowerCase() === "content-type") continue;
    lines.push(`request.Headers.TryAddWithoutValidation(${csStr(h.key)}, ${csStr(h.value)});`);
  }
  if (bodyHasContent(spec)) {
    const ct = combinedHeaders(spec).find((h) => h.key.toLowerCase() === "content-type")?.value ?? (
      spec.bodyType === "json" ? "application/json" : spec.bodyType === "form" ? "application/x-www-form-urlencoded" : "text/plain"
    );
    lines.push(`request.Content = new StringContent(${csStr(spec.body!)}, Encoding.UTF8, ${csStr(ct)});`);
  }
  lines.push(`var res = await client.SendAsync(request);`);
  lines.push(`Console.WriteLine((int)res.StatusCode);`);
  lines.push(`Console.WriteLine(await res.Content.ReadAsStringAsync());`);
  return lines.join("\n");
}
function csStr(s: string): string { return JSON.stringify(s); }

// ── Rust reqwest ─────────────────────────────────────────────────────

function rustReqwest(spec: RequestSpec): string {
  const lines: string[] = [];
  lines.push(`use reqwest::Client;`);
  lines.push(``);
  lines.push(`#[tokio::main]`);
  lines.push(`async fn main() -> Result<(), Box<dyn std::error::Error>> {`);
  lines.push(`    let client = Client::new();`);
  lines.push(`    let res = client`);
  lines.push(`        .${spec.method.toLowerCase()}(${rsStr(fullUrl(spec))})`);
  for (const h of combinedHeaders(spec)) {
    lines.push(`        .header(${rsStr(h.key)}, ${rsStr(h.value)})`);
  }
  if (bodyHasContent(spec)) {
    if (spec.bodyType === "json") {
      const v = tryJson(spec.body!);
      if (v && typeof v === "object") {
        lines.push(`        .json(&serde_json::json!(${spec.body}))`);
      } else {
        lines.push(`        .body(${rsStr(spec.body!)})`);
      }
    } else {
      lines.push(`        .body(${rsStr(spec.body!)})`);
    }
  }
  lines.push(`        .send()`);
  lines.push(`        .await?;`);
  lines.push(`    println!("{}", res.status());`);
  lines.push(`    println!("{}", res.text().await?);`);
  lines.push(`    Ok(())`);
  lines.push(`}`);
  return lines.join("\n");
}
function rsStr(s: string): string { return JSON.stringify(s); }

// ── Swift URLSession ─────────────────────────────────────────────────

function swiftUrlSession(spec: RequestSpec): string {
  const lines: string[] = [];
  lines.push(`import Foundation`);
  lines.push(``);
  lines.push(`let url = URL(string: ${swStr(fullUrl(spec))})!`);
  lines.push(`var request = URLRequest(url: url)`);
  lines.push(`request.httpMethod = ${swStr(spec.method)}`);
  for (const h of combinedHeaders(spec)) {
    lines.push(`request.setValue(${swStr(h.value)}, forHTTPHeaderField: ${swStr(h.key)})`);
  }
  if (bodyHasContent(spec)) {
    lines.push(`request.httpBody = ${swStr(spec.body!)}.data(using: .utf8)`);
  }
  lines.push(``);
  lines.push(`let (data, response) = try await URLSession.shared.data(for: request)`);
  lines.push(`if let http = response as? HTTPURLResponse { print(http.statusCode) }`);
  lines.push(`print(String(data: data, encoding: .utf8) ?? "")`);
  return lines.join("\n");
}
function swStr(s: string): string { return JSON.stringify(s); }

// ── PowerShell ───────────────────────────────────────────────────────

function powershell(spec: RequestSpec): string {
  const lines: string[] = [];
  const headers = combinedHeaders(spec);
  if (headers.length) {
    lines.push(`$headers = @{`);
    for (const h of headers) lines.push(`    ${psStr(h.key)} = ${psStr(h.value)}`);
    lines.push(`}`);
  }
  if (bodyHasContent(spec)) {
    lines.push(`$body = ${psStr(spec.body!)}`);
  }
  const args: string[] = [
    `-Method ${spec.method}`,
    `-Uri ${psStr(fullUrl(spec))}`,
  ];
  if (headers.length) args.push("-Headers $headers");
  if (bodyHasContent(spec)) {
    args.push("-Body $body");
    const ct = headers.find((h) => h.key.toLowerCase() === "content-type")?.value;
    if (ct) args.push(`-ContentType ${psStr(ct)}`);
  }
  lines.push(``, `$res = Invoke-RestMethod \``);
  for (let i = 0; i < args.length; i++) {
    lines.push(`  ${args[i]}${i === args.length - 1 ? "" : " \\"}`);
  }
  lines.push(`$res | ConvertTo-Json -Depth 5`);
  return lines.join("\n");
}
function psStr(s: string): string {
  // PowerShell single-quoted: '' is the only escape for an embedded single-quote.
  return "'" + s.replace(/'/g, "''") + "'";
}

// ── Registry ─────────────────────────────────────────────────────────

export const generators: Generator[] = [
  // curl lives in its own package; we adapt our spec into its shape so
  // the picker can offer it like any other language. The KV `enabled`
  // flag is optional here but required there, so we default it.
  { id: "curl", label: "cURL", lang: "shell", ext: "sh", generate: (s) => emitCurl(toCurlSpec(s)) },
  { id: "javascript-fetch",  label: "JavaScript — fetch",  lang: "javascript", ext: "js",  generate: (s) => jsFetch(s) },
  { id: "typescript-fetch",  label: "TypeScript — fetch",  lang: "typescript", ext: "ts",  generate: (s) => jsFetch(s, { typed: true }) },
  { id: "javascript-axios",  label: "JavaScript — axios",  lang: "javascript", ext: "js",  hint: "npm install axios",       generate: axiosGen },
  { id: "python-requests",   label: "Python — requests",   lang: "python",     ext: "py",  hint: "pip install requests",    generate: pyRequests },
  { id: "python-httpx",      label: "Python — httpx",      lang: "python",     ext: "py",  hint: "pip install httpx",       generate: pyHttpx },
  { id: "go-net-http",       label: "Go — net/http",       lang: "go",         ext: "go",  generate: goNetHttp },
  { id: "ruby-net-http",     label: "Ruby — Net::HTTP",    lang: "ruby",       ext: "rb",  generate: rubyNetHttp },
  { id: "php-guzzle",        label: "PHP — Guzzle",        lang: "php",        ext: "php", hint: "composer require guzzlehttp/guzzle", generate: phpGuzzle },
  { id: "java-http-client",  label: "Java — HttpClient",   lang: "java",       ext: "java", hint: "Requires Java 11+",      generate: javaHttp },
  { id: "csharp-httpclient", label: "C# — HttpClient",     lang: "csharp",     ext: "cs",  generate: csharpHttp },
  { id: "rust-reqwest",      label: "Rust — reqwest",      lang: "rust",       ext: "rs",  hint: "[dependencies]\\nreqwest = { version = \"0.12\", features = [\"json\"] }\\ntokio = { version = \"1\", features = [\"full\"] }", generate: rustReqwest },
  { id: "swift-urlsession",  label: "Swift — URLSession",  lang: "swift",      ext: "swift", generate: swiftUrlSession },
  { id: "powershell",        label: "PowerShell — Invoke-RestMethod", lang: "powershell", ext: "ps1", generate: powershell },
];

export function generate(id: string, spec: RequestSpec): string {
  const g = generators.find((g) => g.id === id);
  if (!g) throw new Error(`unknown generator: ${id}`);
  return g.generate(spec);
}

// Bridge: @rest/curl's RequestSpec requires `enabled` on each KV and
// adds a few extra knobs (followRedirects, insecure, unsupported). We
// default everything sensibly so the curl generator can stay a pure
// adapter — no behavior change vs editor-driven curl emission.
function toCurlSpec(s: RequestSpec): CurlSpec {
  return {
    name: s.name,
    method: s.method,
    url: s.url,
    headers: s.headers.map((h) => ({ key: h.key, value: h.value, enabled: h.enabled !== false })),
    query:   s.query.map((q)   => ({ key: q.key, value: q.value, enabled: q.enabled !== false })),
    bodyType: s.bodyType,
    body: s.body,
    auth: s.auth ?? { kind: "none", fields: {} },
    followRedirects: true,
    insecure: false,
    unsupported: [],
  };
}
